import { useCallback } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

import { CompanyData } from '~/data/company';
import { useCompanyData } from '~/data/company/useCompanyData';
import { CostCenter, useAllCostCenters } from '~/data/costCenters';
import { EmpPosition } from '~/data/employee';
import { EmployeesIndexed } from '~/data/employees';
import { AllEnums, useAllEnums } from '~/data/enums';
import { LegalEntity, useAllLegalEntities } from '~/data/legalEntity';
import { OrgTreeData, useOrgTree } from '~/data/orgunits';
import { getManagerIds } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data';
import { PersonnelGroups } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import { useSWR } from '~/hooks/useSWR';
import { getENVs } from '~/utils/helper';

export type OverlappingEmpForm = {
  overlappingEmployment: OverlappingEmploymentFormType;
};

export type Benefit = {
  fBenefitProposalType: string;
  fBenefitProposalStartDate: string;
  fBenefitProposalEndDate: string;
  fBenefitProposalAmount?: number;
  fBenefitProposalTaxableAmount?: number;
  fBenefitProposalFrequency: string;
  fBenefitProposalInfo: string;
  fEmplAttachmentId: string;
  fEmplAttachmentName: string;
  fEmplAttachment?: File;
};

export type Allowance = {
  fAllowanceProposalType: string;
  fAllowanceProposalStartDate: string;
  fAllowanceProposalEndDate: string;
  fAllowanceProposalAmount?: number;
  fAllowanceProposalTaxableAmount?: number;
  fAllowanceProposalFrequency: string;
  fAllowanceProposalInfo: string;
};

export type StartingSalary = {
  fSalaryProposalFrequency: string;
  fSalaryProposalStartDate: string;
  fSalaryProposalSalary: string;
  fSalaryProposalSalaryType: string;
  fSalaryProposalSalaryClass: string;
  fSalaryProposalChangeReason: string;
  fSalaryProposalInfo: string;
  fEmplAttachmentId: string;
  fEmplAttachmentName: string;
  fEmplAttachment?: File | null;
  fSalaryProposalComponents: {
    fSalaryComp1Amount: string;
    fSalaryComp1StartDate: string;
    fSalaryComp2Amount: string;
    fSalaryComp2StartDate: string;
    fSalaryComp3Amount: string;
    fSalaryComp3StartDate: string;
    fSalaryComp4Amount: string;
    fSalaryComp4StartDate: string;
  };
};

export interface OverlappingEmploymentFormType {
  basicData: {
    fEmpLegalEntity: string;
    fEmpBusinessUnit1: string;
    fInternalPositionManager: string;
    fEmplType: string;
    fEmplDetailStatus: string;
    fEmplDateStart: string;
    fEmplDateArrival: string | null;
    fEmplTrialPeriod: string;
    fEmplTrialPeriodEndDate: string | null;
    fEmplFixedTermReason: string;
    fEmplPlannedEndDate: string;
    fEmplLastDateEmployment: string | null;
    fEmplTerminationDate: string | null;
    fEmplTerminationReason: string;
    fEmplCurrency: string | null;
    fInternalPositionPosition: string;
    fEmpJobTitle: string;
    fEmpCostCenter: string;
    fEmpAddlCostCenters: CostCenter[] | null;
  };
  contact: {
    fEmpPhoneWork: string;
    fEmpPhoneMob: string;
    fEmpEmailWork: string;
    fEmpOfficeLoc: string;
    fEmpOfficeFloor: string;
    fEmpOfficeRoom: string;
  };
  employmentDetails: {
    fEmplDetailJobGrade: string | undefined;
    fEmplDetailTaxNumber: string;
    fEmplInfo: string;
    fEmplDetailVacBalCalcSixDays: number | `${number}` | '';
    fEmplDetailIsPending: boolean;
    fEmplDetailStartDate: string;
    fEmplDetailCalcHoursBalance: boolean;
  } & PersonnelGroups;
  startingSalary: StartingSalary;
  currentWorkingHours: {
    fWorkHoursFtHoursPerWeek: number | '';
    fWorkHoursPtHoursPerWeek: number | '';
    fWorkHoursPtPercentage: number | '';
    fWorkHoursStartDate: string;
    fWorkHoursEndDate: string;
    fWorkHoursType: string;
    fWorkHoursPtReason: string;
    fWorkHoursInfo: string;
  };
  benefit: Benefit[];
  allowance: Allowance[];
  customData: { [x: string]: string };
}

type EmploymentDataObject = {
  empPosition: EmpPosition;
  allEmployees: EmployeesIndexed;
};

export type OverlappingEmploymentFormValues = {
  formProps: UseFormReturn<OverlappingEmpForm>;
  helperValues: {
    initialFormValues: OverlappingEmpForm;
    legalEntities: LegalEntity[];
    orgTree: OrgTreeData[];
    managers: number[] | undefined;
    allEmployees: EmployeesIndexed;
    allEnums: AllEnums;
    allEnumsLoading: boolean;
    currencies: string[];
    costCenterValues: CostCenter[];
    companyData: CompanyData;
    formTemplatesEnabled: string;
  };
};

export const useOverlappingEmploymentForm = ({
  empPosition,
  allEmployees,
}: EmploymentDataObject): OverlappingEmploymentFormValues => {
  const { legalEntities } = useAllLegalEntities();
  const { orgTree } = useOrgTree();
  const { companyData } = useCompanyData();
  const { allEnums, isLoading: allEnumsLoading } = useAllEnums();
  const { costCenters } = useAllCostCenters();

  const formTemplatesEnabled = getENVs('REACT_APP_ENABLE_EMPLOYMENT_TEMPLATES');

  const initialFormValues: OverlappingEmpForm = {
    overlappingEmployment: {
      basicData: {
        fEmpLegalEntity: '',
        fEmpBusinessUnit1: '',
        fInternalPositionManager: '',
        fEmplType: '',
        fEmplDetailStatus: '',
        fEmplDateStart: '',
        fEmplDateArrival: '',
        fEmplTrialPeriod: '',
        fEmplTrialPeriodEndDate: '',
        fEmplFixedTermReason: '',
        fEmplPlannedEndDate: '',
        fEmplLastDateEmployment: '',
        fEmplTerminationDate: '',
        fEmplTerminationReason: '',
        fEmplCurrency: null,
        fInternalPositionPosition: '',
        fEmpJobTitle: '',
        fEmpCostCenter: '',
        fEmpAddlCostCenters: null,
      },
      contact: {
        fEmpPhoneWork: '',
        fEmpPhoneMob: '',
        fEmpEmailWork: '',
        fEmpOfficeLoc: '',
        fEmpOfficeFloor: '',
        fEmpOfficeRoom: '',
      },
      employmentDetails: {
        fEmplDetailJobGrade: '',
        fEmplDetailTaxNumber: '',
        fEmplInfo: '',
        fEmplDetailVacBalCalcSixDays: '',
        fEmplDetailIsPending: false,
        fEmplDetailStartDate: '',
        fEmplDetailCalcHoursBalance: false,
        fEmplDetailPersonnelGroup1: '',
        fEmplDetailPersonnelGroup2: '',
        fEmplDetailPersonnelGroup3: '',
        fEmplDetailPersonnelGroup4: '',
        fEmplDetailPersonnelGroup5: '',
      },
      startingSalary: {
        fSalaryProposalFrequency: '',
        fSalaryProposalStartDate: '',
        fSalaryProposalSalary: '',
        fSalaryProposalSalaryType: '',
        fSalaryProposalSalaryClass: '',
        fSalaryProposalChangeReason: '',
        fSalaryProposalInfo: '',
        fEmplAttachmentId: '-1',
        fEmplAttachmentName: '',
        fEmplAttachment: null,
        fSalaryProposalComponents: {
          fSalaryComp1Amount: '',
          fSalaryComp1StartDate: '',
          fSalaryComp2Amount: '',
          fSalaryComp2StartDate: '',
          fSalaryComp3Amount: '',
          fSalaryComp3StartDate: '',
          fSalaryComp4Amount: '',
          fSalaryComp4StartDate: '',
        },
      },
      currentWorkingHours: {
        fWorkHoursFtHoursPerWeek: '',
        fWorkHoursPtHoursPerWeek: '',
        fWorkHoursPtPercentage: '',
        fWorkHoursStartDate: '',
        fWorkHoursEndDate: '',
        fWorkHoursType: '',
        fWorkHoursPtReason: '',
        fWorkHoursInfo: '',
      },
      benefit: [],
      allowance: [],
      customData: {},
    },
  };

  const currencies = companyData.currencies.fComSupportedCurrencies;

  // Data: Managers
  const { data: managers } = useSWR<number[]>(
    'managers',
    () => getManagerIds(),
    {
      revalidateOnFocus: false,
    },
  );

  const getCostCenterValues = useCallback(
    (positionUnit: number) => {
      const targetPU = positionUnit;

      const costCentersFiltered = costCenters.filter(
        (cc) =>
          cc.hasOwnProperty('fCostCenterUnits') &&
          cc.fCostCenterUnits.indexOf(targetPU) > -1,
      );
      return costCentersFiltered.length ? costCentersFiltered : costCenters;
    },
    [costCenters],
  );
  const costCenterValues = getCostCenterValues(
    empPosition.fInternalPositionUnit,
  );

  const formProps = useForm<OverlappingEmpForm>({
    defaultValues: initialFormValues,
  });

  return {
    formProps,
    helperValues: {
      initialFormValues,
      legalEntities,
      orgTree,
      managers,
      allEmployees,
      allEnums,
      allEnumsLoading,
      currencies,
      costCenterValues,
      companyData,
      formTemplatesEnabled,
    },
  };
};
